<template lang="pug">
.input.timeline(v-if="steps.length")
  label.label Roadmap timeline
  .timeline-help(v-if="!isReadOnly") You can update step years, up to 2035. Drag and drop recommendations from one step to another, and drag a step title to reorder the timeline.

  .steps-list
    .step-row.special-row
      .timeline-dot
      .main
        .rowline
          .arrow-tag(
            :style="{ backgroundColor: beforeRatingColor, '--arrow-color': beforeRatingColor }"
          ) {{ beforeRatingCode ?? "Current" }}
          .name Current rating

    .step-row(
      v-for="step in steps"
      :key="step.key"
      :class="{ 'is-step-drop-target': isStepDropTarget(step.index), 'is-step-drop-allowed': isPotentialStepDropTarget(step.index) }"
      @dragover="onStepDragOver(step.index, $event)"
      @drop="onStepDrop(step.index, $event)"
    )
      .timeline-dot
      .main
        .rowline
          .step-heading
            button.step-handle(
              v-if="isEditable && canDragStep(step.index)"
              type="button"
              draggable="true"
              aria-label="Reorder step"
              @dragstart="onStepDragStart(step.index, $event)"
              @dragend="onStepDragEnd"
            )
              span.step-handle-icon(aria-hidden="true")
              span.name Step {{ step.index + 1 }}
            .name(v-else) Step {{ step.index + 1 }}
          .year-wrap
            .year-input-row
              span.year-label Year
              Dropdown(
                v-if="!isReadOnly"
                :modelValue="step.displayedYear"
                :options="step.yearOptions"
                :class="['year-select', { 'year-select-error': step.hasNoValidYearOptions }]"
                @update:modelValue="emit('update-step-year', step.index, $event)"
              )
              b.year-value(v-else) {{ step.displayedYear ?? 'N/A' }}
            span.step-duration(v-if="step.durationLabel") {{ step.durationLabel }}
            span.step-year-feedback.is-warning(v-if="step.leavesFollowingStepsInvalid") This choice leaves no valid years for later steps
            span.step-year-feedback.is-error(v-else-if="step.hasNoValidYearOptions") Choose an earlier year in a previous step

        .measures(
          :class="{ 'is-dropzone-active': isDropzoneActive(step.index), 'is-dropzone-allowed': isPotentialMeasureContainerDropTarget(step.index), 'is-empty': isStepEmpty(step) }"
          @dragover="onMeasuresContainerDragOver(step.index, $event)"
          @drop="onMeasuresContainerDrop(step.index, $event)"
        )
          .empty-measures-placeholder(v-if="isStepEmpty(step)")
            .empty-step-message
              span.pi.pi-plus-circle(aria-hidden="true")
              span Drag and drop a recommendation into this step
            button.empty-step-remove(
              v-if="isEditable && step.canRemoveEmptyStep"
              type="button"
              @click="emit('remove-step', step.index)"
            ) Remove step
          .measure-row(
            v-for="(measure, measureIndex) in step.measures"
            :key="measure.key"
            :class="getMeasureRowClass(step.index, measureIndex)"
            :draggable="isEditable && canDragMeasure(step.index, measureIndex)"
            @dragstart="onMeasureDragStart(step.index, measureIndex, $event)"
            @dragover="onMeasureDragOver(step.index, measureIndex, $event)"
            @drop="onMeasureDrop(step.index, measureIndex, $event)"
            @dragend="onMeasureDragEnd"
          )
            .m-content
              .m-left
                .card-icon
                  GammaIcon(
                    v-if="measure.icon || measure.defaultIcon"
                    :src="measure.icon"
                    :default-src="measure.defaultIcon"
                    class="passport-icon"
                  )
                .m-main
                  .m-name {{ measure.name }}
                  .m-desc {{ measure.description }}
                  .comfort-line(v-if="typeof measure.comfort === 'number'")
                    span.label Comfort:
                    span.comfort-indicator {{ getComfortDots(measure.comfort) }}
                    span.cost-text(v-if="measure.cost")
                      span.separator |
                      | {{ measure.cost }}

              .m-recommendations(v-if="measure.recommendationNames.length")
                .m-recommendation(v-for="name in measure.recommendationNames" :key="name") {{ name }}

    .add-step-row(v-if="isEditable && canAddStep")
      button.add-step-button(type="button" @click="emit('add-step')")
        span Add step
        span.pi.pi-plus(aria-hidden="true")

    .step-row.special-row
      .timeline-dot
      .main
        .rowline
          .arrow-tag(
            :style="{ backgroundColor: afterRatingColor, '--arrow-color': afterRatingColor }"
          ) {{ afterRatingCode ?? "New" }}
          .name New rating
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import Dropdown from "primevue/dropdown";
import GammaIcon from "@/components/icons/GammaIcon.vue";
import { getRatingColor } from "@/helpers";

type TimelineMeasure = {
  key: string
  name: string
  description: string
  comfort?: number
  cost?: string
  icon?: string | null
  defaultIcon?: string | null
  recommendationNames: string[]
};

type TimelineStep = {
  key: string
  index: number
  displayedYear?: number
  yearOptions: number[]
  durationLabel: string | null
  leavesFollowingStepsInvalid: boolean
  hasNoValidYearOptions: boolean
  canRemoveEmptyStep: boolean
  measures: TimelineMeasure[]
};

type TimelineMeasureMovePayload = {
  sourceStepIndex: number
  sourceMeasureIndex: number
  targetStepIndex: number
  targetMeasureIndex?: number
};

type TimelineStepMovePayload = {
  sourceStepIndex: number
  targetStepIndex: number
};

const props = defineProps<{
  steps: TimelineStep[]
  canAddStep: boolean
  isReadOnly: boolean
  canMoveMeasure?: (payload: TimelineMeasureMovePayload) => boolean
  canMoveStep?: (payload: TimelineStepMovePayload) => boolean
  beforeRatingCode?: string
  afterRatingCode?: string
}>();

const emit = defineEmits<{
  (e: "update-step-year", index: number, year: number): void
  (e: "move-step", payload: TimelineStepMovePayload): void
  (e: "move-measure", payload: TimelineMeasureMovePayload): void
  (e: "add-step"): void
  (e: "remove-step", index: number): void
}>();

type DragMeasurePayload = {
  sourceStepIndex: number
  sourceMeasureIndex: number
};

type DragMeasureTarget = {
  stepIndex: number
  insertIndex?: number
};

type DragStepPayload = {
  sourceStepIndex: number
};

type DragStepTarget = {
  hoveredStepIndex: number
};

const draggedMeasure = ref<DragMeasurePayload | null>(null);
const dragTarget = ref<DragMeasureTarget | null>(null);
const draggedStep = ref<DragStepPayload | null>(null);
const stepDragTarget = ref<DragStepTarget | null>(null);
const isEditable = computed(() => !props.isReadOnly);

const beforeRatingColor = computed(() =>
    getRatingColor(props.beforeRatingCode) || "var(--berwow-secondary-color)"
);
const afterRatingColor = computed(() =>
    getRatingColor(props.afterRatingCode) || "var(--berwow-secondary-color)"
);

function isStepEmpty(step: TimelineStep) {
  return step.measures.length === 0;
}

function getComfortDots(level: number) {
  const safe = Math.max(0, Math.min(5, level || 0));
  return "\u25CF".repeat(safe) + "\u25CB".repeat(5 - safe);
}

function setMoveDragData(event: DragEvent, value: string) {
  if (!event.dataTransfer) return;
  event.dataTransfer.effectAllowed = "move";
  event.dataTransfer.setData("text/plain", value);
}

function enableMoveDrop(event: DragEvent) {
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "move";
  }
}

function onStepDragStart(stepIndex: number, event: DragEvent) {
  if (!isEditable.value || !canDragStep(stepIndex)) return;

  draggedStep.value = {
    sourceStepIndex: stepIndex
  };
  stepDragTarget.value = null;
  setMoveDragData(event, `step:${stepIndex}`);
}

function onStepDragOver(stepIndex: number, event: DragEvent) {
  if (!canHandleStepDrop(event)) return;
  if (!isValidStepMove(stepIndex)) return;
  setStepDragTarget(stepIndex);
}

function onStepDrop(stepIndex: number, event: DragEvent) {
  if (!hasActiveStepDrag()) return;
  event.preventDefault();

  const targetStepIndex = stepDragTarget.value?.hoveredStepIndex ?? stepIndex;
  commitStepDrop(targetStepIndex);
}

function onStepDragEnd() {
  clearStepDragState();
}

function commitStepDrop(targetStepIndex: number) {
  if (!draggedStep.value) return;

  const { sourceStepIndex } = draggedStep.value;
  if (
    sourceStepIndex !== targetStepIndex &&
    isValidStepMove(targetStepIndex)
  ) {
    emit("move-step", {
      sourceStepIndex,
      targetStepIndex
    });
  }

  clearStepDragState();
}

function onMeasureDragStart(stepIndex: number, measureIndex: number, event: DragEvent) {
  if (!isEditable.value || !canDragMeasure(stepIndex, measureIndex)) return;

  draggedMeasure.value = {
    sourceStepIndex: stepIndex,
    sourceMeasureIndex: measureIndex
  };
  dragTarget.value = null;
  setMoveDragData(event, `${stepIndex}:${measureIndex}`);
}

function onMeasureDragOver(stepIndex: number, measureIndex: number, event: DragEvent) {
  if (!canHandleMeasureDrop(event)) return;
  if (!isValidMeasureMove(stepIndex, measureIndex)) return;
  setDragTarget(stepIndex, measureIndex);
}

function onMeasuresContainerDragOver(stepIndex: number, event: DragEvent) {
  if (!canHandleMeasureDrop(event)) return;
  const insertIndex = getStepInsertIndex(stepIndex);
  if (!isValidMeasureMove(stepIndex, insertIndex)) return;
  setDragTarget(stepIndex, insertIndex);
}

function onMeasureDrop(stepIndex: number, measureIndex: number, event: DragEvent) {
  if (!hasActiveMeasureDrag()) return;
  event.preventDefault();

  commitMeasureDrop(stepIndex, measureIndex);
}

function onMeasuresContainerDrop(stepIndex: number, event: DragEvent) {
  if (!hasActiveMeasureDrag()) return;
  event.preventDefault();

  commitMeasureDrop(stepIndex, getStepInsertIndex(stepIndex));
}

function commitMeasureDrop(targetStepIndex: number, targetMeasureIndex?: number) {
  if (!draggedMeasure.value) return;

  const { sourceStepIndex, sourceMeasureIndex } = draggedMeasure.value;
  const payload: TimelineMeasureMovePayload = {
    sourceStepIndex,
    sourceMeasureIndex,
    targetStepIndex,
    targetMeasureIndex
  };

  const isSameStep = payload.sourceStepIndex === payload.targetStepIndex;
  const isSamePosition =
      isSameStep &&
      (payload.targetMeasureIndex === payload.sourceMeasureIndex ||
          payload.targetMeasureIndex === payload.sourceMeasureIndex + 1);

  if (!isSamePosition && isValidMeasurePayload(payload)) {
    emit("move-measure", payload);
  }

  clearDragState();
}

function onMeasureDragEnd() {
  clearDragState();
}

function clearDragState() {
  draggedMeasure.value = null;
  dragTarget.value = null;
}

function clearStepDragState() {
  draggedStep.value = null;
  stepDragTarget.value = null;
}

function hasActiveMeasureDrag() {
  return isEditable.value && !!draggedMeasure.value && !draggedStep.value;
}

function hasActiveStepDrag() {
  return isEditable.value && !!draggedStep.value && !draggedMeasure.value;
}

function canHandleMeasureDrop(event: DragEvent) {
  if (!hasActiveMeasureDrag()) return false;
  enableMoveDrop(event);
  return true;
}

function getStepInsertIndex(stepIndex: number) {
  return props.steps[stepIndex]?.measures.length ?? 0;
}

function setDragTarget(stepIndex: number, insertIndex: number) {
  dragTarget.value = {
    stepIndex,
    insertIndex
  };
}

function canHandleStepDrop(event: DragEvent) {
  if (!hasActiveStepDrag()) return false;
  enableMoveDrop(event);
  return true;
}

function isSameMeasurePosition(payload: TimelineMeasureMovePayload) {
  const isSameStep = payload.sourceStepIndex === payload.targetStepIndex;

  return (
    isSameStep &&
    (payload.targetMeasureIndex === payload.sourceMeasureIndex ||
      payload.targetMeasureIndex === payload.sourceMeasureIndex + 1)
  );
}

function isValidStepMove(targetStepIndex: number) {
  if (!draggedStep.value) return false;
  if (!props.canMoveStep) return true;

  return props.canMoveStep({
    sourceStepIndex: draggedStep.value.sourceStepIndex,
    targetStepIndex
  });
}

function isValidMeasurePayload(payload: TimelineMeasureMovePayload) {
  if (isSameMeasurePosition(payload)) return false;
  if (!props.canMoveMeasure) return true;
  return props.canMoveMeasure(payload);
}

function isValidMeasureMove(targetStepIndex: number, targetMeasureIndex?: number) {
  if (!draggedMeasure.value) return false;

  return isValidMeasurePayload({
    sourceStepIndex: draggedMeasure.value.sourceStepIndex,
    sourceMeasureIndex: draggedMeasure.value.sourceMeasureIndex,
    targetStepIndex,
    targetMeasureIndex
  });
}

function canDragStep(stepIndex: number) {
  if (!isEditable.value) return false;

  return props.steps.some(
    (step) =>
      step.index !== stepIndex &&
      (!props.canMoveStep ||
        props.canMoveStep({
          sourceStepIndex: stepIndex,
          targetStepIndex: step.index
        }))
  );
}

function canDragMeasure(stepIndex: number, measureIndex: number) {
  if (!isEditable.value) return false;

  return props.steps.some((step) =>
    Array.from({ length: step.measures.length + 1 }, (_, insertIndex) =>
      isValidMeasurePayload({
        sourceStepIndex: stepIndex,
        sourceMeasureIndex: measureIndex,
        targetStepIndex: step.index,
        targetMeasureIndex: insertIndex
      })
    ).some(Boolean)
  );
}

function isPotentialStepDropTarget(stepIndex: number) {
  return (
    !!draggedStep.value &&
    !draggedMeasure.value &&
    draggedStep.value.sourceStepIndex !== stepIndex &&
    isValidStepMove(stepIndex)
  );
}

function isPotentialMeasureDropTarget(stepIndex: number, measureIndex: number) {
  return hasActiveMeasureDrag() && isValidMeasureMove(stepIndex, measureIndex);
}

function isPotentialMeasureContainerDropTarget(stepIndex: number) {
  return hasActiveMeasureDrag() && isValidMeasureMove(stepIndex, getStepInsertIndex(stepIndex));
}

function setStepDragTarget(stepIndex: number) {
  stepDragTarget.value = {
    hoveredStepIndex: stepIndex
  };
}

function isDropTarget(stepIndex: number, measureIndex: number) {
  return (
      hasActiveMeasureDrag() &&
      dragTarget.value?.stepIndex === stepIndex &&
      dragTarget.value?.insertIndex === measureIndex
  );
}

function isDropzoneActive(stepIndex: number) {
  return (
      hasActiveMeasureDrag() &&
      dragTarget.value?.stepIndex === stepIndex
  );
}

function isDraggedMeasure(stepIndex: number, measureIndex: number) {
  return (
      hasActiveMeasureDrag() &&
      draggedMeasure.value?.sourceStepIndex === stepIndex &&
      draggedMeasure.value?.sourceMeasureIndex === measureIndex
  );
}

function getMeasureRowClass(stepIndex: number, measureIndex: number) {
  return {
    "is-drop-target": isDropTarget(stepIndex, measureIndex),
    "is-drop-allowed": isPotentialMeasureDropTarget(stepIndex, measureIndex),
    "is-dragging": isDraggedMeasure(stepIndex, measureIndex),
    "is-draggable": canDragMeasure(stepIndex, measureIndex)
  };
}

function isStepDropTarget(stepIndex: number) {
  return (
      hasActiveStepDrag() &&
      stepDragTarget.value?.hoveredStepIndex === stepIndex
  );
}

</script>

<style scoped lang="scss">
.input {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;

  &:not(:last-child) {
    margin-bottom: 1rem;
  }

  label {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    justify-content: flex-start;
    font-size: 1.2rem;
  }

  &.timeline {
    --timeline-row-width: 55rem;

    .timeline-help {
      color: $font-color-secondary;
      margin-top: -0.5rem;
    }

    .steps-list {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 0.9rem;
      padding-left: 3rem;
      box-sizing: border-box;
      width: 100%;
      max-width: calc(var(--timeline-row-width) + 3rem);
      margin: 0 auto;

      &::before {
        content: "";
        position: absolute;
        left: 1rem;
        top: 2rem;
        bottom: 1rem;
        width: 2px;
        height: calc(100% - 4rem);
        background: $secondary-color;
      }

      .add-step-row {
        width: 100%;
        max-width: var(--timeline-row-width);
      }

      .add-step-button {
        width: 100%;
        min-height: 3.5rem;
        padding: 0.55rem 1rem;
        border-radius: 10px;
        border: 1px dashed $border-color;
        background: transparent;
        color: $font-color-secondary;
        font-weight: 400;
        font-size: 1rem;
        letter-spacing: 0.02em;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

        &:hover {
          border-color: $secondary-color;
          color: $font-color-default;
        }
      }
    }

    .step-row {
      position: relative;
      border: 1px solid $border-color;
      border-radius: 10px;
      display: grid;
      gap: 0.75rem;
      align-items: start;
      background: $background-color-default;
      width: 100%;
      max-width: var(--timeline-row-width);
      transition: box-shadow 0.2s ease, border-color 0.2s ease;

      &.is-step-drop-target {
        border-color: $secondary-color;
        box-shadow: inset 0 0 0 1px $secondary-color;
      }

      &.is-step-drop-allowed:not(.is-step-drop-target) {
        border-color: color-mix(in srgb, $secondary-color 42%, $border-color);
        box-shadow:
          inset 0 0 0 1px color-mix(in srgb, $secondary-color 22%, transparent),
          0 0 0 2px color-mix(in srgb, $secondary-color 10%, transparent);
        background: color-mix(in srgb, $secondary-color 4%, $background-color-default);
      }

      &.special-row {
        border: none;
        padding-left: 0;

        .timeline-dot {
          left: -2.4rem;
        }
      }

      .timeline-dot {
        position: absolute;
        left: -2.5rem;
        top: calc(50% - 4px);
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        background: $secondary-color;
        border: 3px solid $secondary-color;
        z-index: 2;
      }

      .main {
        display: grid;
        gap: 0.6rem;
        padding: 1rem 1.25rem 1rem 1.8rem;
      }

      .rowline {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        flex-wrap: wrap;
        position: relative;

        .step-heading {
          position: relative;
          display: inline-flex;
          align-items: center;
          min-height: 2rem;

          .name {
            color: var(--berwow-font-color-title);
          }
        }

        .name {
          font-weight: 700;
          font-size: 1.5rem;
        }

        .step-handle {
          border: 0;
          background: transparent;
          color: inherit;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: flex-start;
          gap: 0.7rem;
          cursor: move;
          position: relative;
          transition: color 0.2s ease, opacity 0.2s ease;
          margin-left: -1rem;
          padding-left: 1rem;
          min-height: 2rem;

          &:hover {
            color: $secondary-color;
            opacity: 1;

            .step-handle-icon::before,
            .step-handle-icon::after {
              background: $secondary-color;
            }
          }

          &:active {
            cursor: grabbing;
          }

          .step-handle-icon {
            position: relative;
            flex: 0 0 auto;
            width: 0.85rem;
            height: 0.65rem;
            opacity: 0.6;

            &::before,
            &::after {
              content: "";
              position: absolute;
              left: 0;
              width: 100%;
              height: 2px;
              border-radius: 999px;
              background: $font-color-secondary;
            }

            &::before {
              top: 0.1rem;
            }

            &::after {
              bottom: 0.1rem;
            }
          }
        }

        .arrow-tag {
          position: relative;
          display: inline-flex;
          align-items: center;
          height: 35px;
          padding: 0 12px 0 14px;
          color: $font-color-contrast;
          font-size: 1.5rem;
          font-weight: 600;
          line-height: 1;
          border-radius: 0 5px 5px 0;
          margin-right: 0.75rem;
        }

        .arrow-tag::before {
          content: "";
          position: absolute;
          top: 0;
          left: -17px;
          width: 0;
          height: 0;
          border-top: 17px solid transparent;
          border-bottom: 17px solid transparent;
          border-right: 17px solid var(--arrow-color);
        }

        .year-wrap {
          display: inline-flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.35rem;
          margin-left: auto;
          align-self: flex-start;

          .year-input-row {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }

          .year-label {
            opacity: 0.8;
            font-size: 1.2rem;
          }

          .year-select {
            min-width: 9rem;
          }

          :deep(.year-select-error.p-dropdown) {
            border-color: color-mix(in srgb, $error-color 55%, transparent);
            box-shadow: inset 0 0 0 1px color-mix(in srgb, $error-color 18%, transparent);
          }

          .year-value,
          .year {
            font-weight: 600;
            font-size: 1.4rem;
          }

          .step-duration {
            white-space: nowrap;
            pointer-events: none;
            font-size: 0.85rem;
            color: $font-color-secondary;
            line-height: 1.2;
          }

          .step-year-feedback {
            max-width: 20rem;
            font-size: 0.85rem;
            line-height: 1.3;
            color: $font-color-secondary;
            text-align: right;
            align-self: flex-end;

            &.is-warning {
              display: inline-block;
              color: color-mix(in srgb, $warning-color 55%, $font-color-default);
              background: color-mix(in srgb, $warning-color 14%, transparent);
              border: 1px solid color-mix(in srgb, $warning-color 28%, transparent);
              border-radius: 8px;
              padding: 0.35rem 0.5rem;
              font-weight: 500;
            }

            &.is-error {
              color: $error-color;
            }
          }
        }
      }

      .measures {
        display: grid;
        gap: 0.5rem;
        border: 1px dashed transparent;
        border-radius: 10px;
        transition: border-color 0.2s ease, background-color 0.2s ease;

        &.is-dropzone-active {
          border-color: $secondary-color;
          background: color-mix(in srgb, $font-color-default 2%, transparent);
        }

        &.is-dropzone-allowed:not(.is-dropzone-active) {
          border-color: color-mix(in srgb, $secondary-color 24%, transparent);
          background: color-mix(in srgb, $secondary-color 4%, transparent);
        }

        &.is-empty {
          min-height: 5rem;
          align-content: center;
        }

        .empty-measures-placeholder {
          min-height: 4rem;
          border: 1px dashed color-mix(in srgb, $font-color-default 8%, transparent);
          border-radius: 10px;
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          color: $font-color-secondary;
          font-size: 0.95rem;

          .empty-step-message {
            display: inline-flex;
            align-items: center;
            gap: 0.55rem;
            text-align: center;
          }

          .empty-step-remove {
            border: 0;
            background: transparent;
            padding: 0;
            color: $secondary-color;
            font: inherit;
            line-height: 1;
            text-decoration: underline;
            cursor: pointer;

            &:hover {
              color: $secondary-color-hover;
            }
          }
        }

        .measure-row {
          border-radius: 10px;
          padding: 0.7rem 0.75rem;
          display: block;
          transition: background-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;

          &.is-draggable {
            cursor: grab;
          }

          &.is-draggable:active {
            cursor: grabbing;
          }

          &.is-drop-target {
            box-shadow: inset 0 0 0 2px $secondary-color;
            background: color-mix(in srgb, $font-color-default 3%, transparent);
          }

          &.is-drop-allowed:not(.is-drop-target) {
            box-shadow: inset 0 0 0 1px color-mix(in srgb, $secondary-color 18%, transparent);
            background: color-mix(in srgb, $secondary-color 3%, transparent);
          }

          &.is-dragging {
            opacity: 0.4;
          }

          .m-content {
            display: grid;
            grid-template-columns: minmax(16rem, 16rem) minmax(0, 1fr);
            gap: 1.25rem;
            align-items: center;
            min-width: 0;
            width: min(100%, 42rem);
            margin: 0 auto;
          }

          .m-left {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            min-width: 0;
            order: 2;

            .card-icon {
              flex: 0 0 auto;

              &:deep(.passport-icon) {
                width: 2.2rem;
                max-width: 2.2rem;
                height: auto;
              }
            }

            .m-main {
              display: grid;
              gap: 0.2rem;
              min-width: 0;

              .m-name {
                font-weight: 600;
              }

              .m-desc {
                opacity: 0.85;
              }

              .comfort-line {
                margin-top: 0.25rem;
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;
                align-items: center;
                font-size: 0.9rem;

                .cost-text {
                  font-weight: 700;

                  .separator {
                    font-weight: normal;
                    margin-right: 0.3rem;
                  }
                }
              }
            }
          }

          .m-recommendations {
            text-align: right;
            border-right: 2px solid var(--berwow-border-color);
            padding-right: 1rem;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 0.75rem;
            min-height: 100%;
            color: $font-color-secondary;

            .m-recommendation {
              line-height: 1.35;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 650px) {
  .input.timeline {
    .steps-list {
      width: 100%;
      padding-left: 2.25rem;
      margin: 0;

      &::before {
        left: 0.8rem;
      }

      .add-step-row {
        width: 100%;
      }
    }

    .step-row {
      width: 100%;

      .timeline-dot {
        left: -1.85rem;
      }

      .measures {
        .measure-row {
          .m-content {
            grid-template-columns: 1fr;
            gap: 0.85rem;
          }

          .m-recommendations {
            order: 2;
            text-align: left;
            border-right: 0;
            border-left: 2px solid var(--berwow-border-color);
            padding-right: 0;
            padding-left: 1rem;
          }

          .m-left {
            order: 1;
          }
        }
      }
    }
  }
}

@media (max-width: 620px) and (min-width: 520px) {
  .input.timeline {
    .steps-list {
      padding-left: 0.5rem;

      &::before {
        left: 0;
      }
    }

    .step-row {
      .timeline-dot {
        left: -0.5rem;
      }

      .main {
        padding-left: 0.75rem;
        padding-right: 1rem;
      }

      .measures {
        .measure-row {
          padding-left: 0;
          padding-right: 0.45rem;

          .m-content {
            grid-template-columns: 1fr;
            gap: 0.65rem;
            width: 100%;
            margin: 0;
          }

          .m-recommendations {
            text-align: left;
            border-right: 0;
            border-left: 2px solid var(--berwow-border-color);
            padding-right: 0;
            padding-left: 0.75rem;
          }
        }
      }
    }
  }
}
</style>
